import {
    Children,
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import {
    Box,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import TuneIcon from '@mui/icons-material/Tune';
import {
    ChartOptionsSheet,
    type ChartOptionsSheetProps,
} from './ChartOptionsSheet';
import { segmentedSx } from './segmented';

// The chart toolbar below 900 px: one row, 44 px targets. The range buttons
// are 46 px wide (the group is 232 px with its own 1 px border, as in the
// mockup) and give way down to 44 px before the two icon buttons do; below
// 372 px the row also borrows 4 px from the card's padding on each side, so
// it fits a 360 px phone.

export interface CompactRange {
    id: string;
    label: string;
    disabled?: boolean;
}

export interface CompactChartToolbarProps<T extends string> {
    ranges: readonly CompactRange[];
    activeRange: string | null;
    onRange: (id: string) => void;
    options: Omit<ChartOptionsSheetProps<T>, 'open' | 'onClose'>;
    fullscreen: boolean;
    onToggleFullscreen: () => void;
    /**
     * Extra controls (the event page's chips for hidden panels), under the
     * row; in full screen, beside the range buttons when the row has the room
     * for all of them (a phone on its side), so the chart keeps their height.
     */
    extra?: ReactNode;
}

// What the row holds besides the extra controls: the range group (232 px),
// and the two icon buttons with the 4 px between them.
const ROW_FIXED_PX = 232 + 44 + 4 + 44;
const EXTRA_GAP_PX = 4;
// Air between the range group and the chips, and between the chips and the
// icon buttons.
const EXTRA_AIR_PX = 16;

const ICON_BUTTON_SX = {
    width: 44,
    height: 44,
    flexShrink: 0,
    color: (theme: Theme) => alpha(theme.palette.text.primary, 0.7),
} as const;

export function CompactChartToolbar<T extends string>({
    ranges,
    activeRange,
    onRange,
    options,
    fullscreen,
    onToggleFullscreen,
    extra,
}: CompactChartToolbarProps<T>) {
    const [sheetOpen, setSheetOpen] = useState(false);
    const hasExtra = Children.toArray(extra).length > 0;

    const rowRef = useRef<HTMLDivElement>(null);
    const extraRef = useRef<HTMLDivElement>(null);
    const [beside, setBeside] = useState(false);
    // Measured in either place (the chips do not shrink): the row takes them
    // when they all fit between the range buttons and the icon buttons. Before
    // paint, so a chip never shows in the wrong place for a frame.
    const place = useCallback(() => {
        const row = rowRef.current;
        const box = extraRef.current;
        if (!fullscreen || !row || !box) {
            setBeside(false);
            return;
        }
        const chips = Array.from(box.children) as HTMLElement[];
        const needed =
            chips.reduce((sum, chip) => sum + chip.offsetWidth, 0) +
            EXTRA_GAP_PX * Math.max(0, chips.length - 1) +
            EXTRA_AIR_PX;
        setBeside(needed <= row.clientWidth - ROW_FIXED_PX);
    }, [fullscreen]);
    useLayoutEffect(place);
    useLayoutEffect(() => {
        const row = rowRef.current;
        if (!row || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(place);
        observer.observe(row);
        return () => observer.disconnect();
    }, [place]);
    const extraBox = (inRow: boolean) => (
        <Box
            ref={extraRef}
            sx={
                inRow
                    ? {
                          display: 'flex',
                          alignItems: 'center',
                          gap: `${EXTRA_GAP_PX}px`,
                          ml: 1,
                          '& > *': { flexShrink: 0 },
                      }
                    : {
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: `${EXTRA_GAP_PX}px`,
                          mb: 1,
                          '& > *': { flexShrink: 0 },
                      }
            }
        >
            {extra}
        </Box>
    );

    return (
        <>
            <Box
                ref={rowRef}
                data-testid="chart-toolbar"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    // Five 44 px ranges (with the group's border), two 44 px icon
                    // buttons and the 4 px between them are 314 px, and a
                    // 360 px phone's card leaves 312: borrow 4 px of the
                    // card's padding on each side until 372 px.
                    mx: -0.5,
                    '@media (min-width: 372px)': { mx: 0 },
                    mb: 1,
                }}
            >
                <ToggleButtonGroup
                    exclusive
                    value={activeRange}
                    aria-label="Time range"
                    onChange={(_, id: string | null) => {
                        if (id) onRange(id);
                    }}
                    sx={[
                        segmentedSx(6),
                        {
                            // 5 x 46 px and the group's own 1 px border
                            flex: '0 1 232px',
                            '& .MuiToggleButton-root': {
                                flex: '1 1 0',
                                minWidth: 44,
                                height: 44,
                                px: 0,
                                fontSize: '14px',
                                fontWeight: 500,
                                textTransform: 'none',
                                color: (theme: Theme) =>
                                    alpha(theme.palette.text.primary, 0.7),
                                '&.Mui-selected, &.Mui-selected:hover': {
                                    fontWeight: 700,
                                    color: 'text.primary',
                                    bgcolor: (theme: Theme) =>
                                        alpha(theme.palette.text.primary, 0.12),
                                },
                            },
                        },
                    ]}
                >
                    {ranges.map(range => (
                        <ToggleButton
                            key={range.id}
                            value={range.id}
                            disabled={range.disabled}
                            aria-label={`Time range: ${range.label}`}
                        >
                            {range.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                {hasExtra && beside && extraBox(true)}
                <Box sx={{ flex: 1 }} />
                <IconButton
                    aria-label="Chart options"
                    onClick={() => setSheetOpen(true)}
                    sx={ICON_BUTTON_SX}
                >
                    <TuneIcon />
                </IconButton>
                <IconButton
                    aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
                    onClick={onToggleFullscreen}
                    sx={{ ...ICON_BUTTON_SX, ml: '4px' }}
                >
                    {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
            </Box>
            {hasExtra && !beside && extraBox(false)}
            <ChartOptionsSheet
                {...options}
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
            />
        </>
    );
}
